import React, { useContext } from "react";
import { StorageContext } from "../contexts/StorageContext";
import ResponseError from "../services/helpers/ResponseError";
import alerts from "./alerts";
import errorStatusMessage from "../services/helpers/errorStatusMessage";
import { api } from "../services";
import { getPlaceDefaults } from "../services/helpers/parseConfigParams";

export interface PlaceOption {
  label: string;
  value: string;
  user?: any;
  params: any;
  defaults?: {
    checkInHour: number;
    checkInMinute: number;
    checkOutHour: number;
    checkOutMinute: number;
  };
}

function useStateCallback(initialState?: any) {
  const [state, setState] = React.useState<any>(initialState);
  const cbRef = React.useRef<any>(null); // mutable ref to store current callback

  const setStateCallback = React.useCallback((state?: any, cb?: any) => {
    cbRef.current = cb; // store passed callback to ref
    setState(state);
  }, []);

  React.useEffect(() => {
    // cb.current is `null` on initial render, so we only execute cb on state *updates*
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null; // reset callback after execution
    }
  }, [state]);

  return [state, setStateCallback];
}

function useConstructor(onBuilding?: any) {
  const created = React.useRef<any>(false);
  if (!created?.current) {
    created.current = true;
    onBuilding();
  }
}

function useMount(onMount?: any, onUnmount?: any) {
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      onMount();
      return onUnmount;
    }
  }, [onMount, onUnmount, mounted]);
}

function useRequest() {
  const [loading, setLoading] = React.useState(false);
  const storage = React.useContext<any>(StorageContext);
  const { logOut } = useContext<any>(StorageContext);
  const [pendingRequests, setPendingRequests] = React.useState(0); // Rastrear requisições pendentes

  async function call(
    storageKey?: any,
    apiRequest?: any,
    callback?: any,
    customError?: (response: any) => void
  ) {
    setPendingRequests((prev) => prev + 1);
    setLoading(true);

    let response;
    try {
      response = await apiRequest;
    } catch (error) {
      console.log(error)
    } finally {
      setPendingRequests((prev) => {
        const newCount = prev - 1;
        if (newCount === 0) {
          setLoading(false);
        }
        return newCount;
      });
    }

    if (response) {
      if (storageKey) {
        storage.addData(storageKey, response);
      }
      if (callback) {
        callback(response);
      }
      if (!response.ok) {
        if (customError) {
          customError(response);
        } else {
          const errorResponse = new ResponseError(response);

          if (response.status === 401) {
            logOut();
          }
          if (errorResponse.message) {
            alerts.alertError(errorResponse.message);
          } else {
            const errorMsg = errorStatusMessage(response);
            alerts.alertError(errorMsg);
          }
        }
      }
    }
    return response;
  }

  return { loading, call };
}

function usePlacesList(condominiumId?: string) {
  const [placesList, setPlacesList] = React.useState<PlaceOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const { call } = useRequest();

  React.useEffect(() => {
    setLoading(true);
    call(
      null,
      api.getPlaces({ page: 0, size: 999999, ...(condominiumId !== undefined && { condominiumId: condominiumId }) }),
      async (response) => {
        if (response.ok) {
          const list = response.data.content.map((item: any) => ({
            label: item.name,
            value: item.id,
            user: item.user,
            type: item.type,
            params: item.configurationParameters,
            defaults: getPlaceDefaults(item.configurationParameters),
          }));
          setPlacesList(list);
        }
        setLoading(false);
      }
    );
  }, [condominiumId]);

  return { placesList, loading };
}

const hooks = {
  useStateCallback,
  useMount,
  useConstructor,
  useRequest,
  usePlacesList,
};
export default hooks;
